import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getMangaById, mangaList } from '@/mocks/manga';
import { genreToSlug } from '@/mocks/categories';
import type { Manga } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const initialManga = id ? getMangaById(id) : undefined;
  const [manga, setManga] = useState<Manga | undefined>(initialManga);
  const [notFound, setNotFound] = useState(!initialManga);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      return;
    }
    const found = getMangaById(id);
    if (!found) {
      setNotFound(true);
      return;
    }
    setManga(found);
    setNotFound(false);

    document.title = `${found.title} - 汗汗漫画 | 免费韩国漫画在线阅读`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `${found.title}——${found.description.substring(0, 120)}。汗汗漫画免费韩国漫画在线阅读，作者：${found.author}。`);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `http://sinotruckcn.com/manga/${id}`);
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: found.title,
      description: found.description,
      author: { '@type': 'Person', name: found.author },
      datePublished: found.updatedAt,
      publisher: {
        '@type': 'Organization',
        name: '汗汗漫画',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `http://sinotruckcn.com/manga/${id}`,
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'http://sinotruckcn.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${found.genres[0]}漫画`,
          item: `http://sinotruckcn.com/category/${genreToSlug[found.genres[0]] || found.genres[0]}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: found.title,
          item: `http://sinotruckcn.com/manga/${id}`,
        },
      ],
    };
    const scriptBreadcrumb = document.createElement('script');
    scriptBreadcrumb.type = 'application/ld+json';
    scriptBreadcrumb.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(scriptBreadcrumb);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      if (document.head.contains(scriptBreadcrumb)) {
        document.head.removeChild(scriptBreadcrumb);
      }
    };
  }, [id]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-600 mb-6">该漫画不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="w-8 h-8 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line text-2xl text-primary-500"></i>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedMangas = mangaList
    .filter((m) => m.id !== manga.id && m.genres.some((g) => manga.genres.includes(g)))
    .slice(0, 5);

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <nav className="w-full px-6 md:px-10 py-4 bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm flex-wrap">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors duration-200">首页</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            </div>
            <Link to={`/category/${genreToSlug[manga.genres[0]] || manga.genres[0]}`} className="text-foreground-500 hover:text-primary-500 transition-colors duration-200">{manga.genres[0]}漫画</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            </div>
            <span className="text-foreground-900 font-medium truncate max-w-[200px]">{manga.title}</span>
          </div>
        </nav>

        <article className="w-full py-10 md:py-14 px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <div className="flex-shrink-0 w-full md:w-72">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src={manga.coverImage}
                    alt={manga.title}
                    title={`${manga.title} - 汗汗漫画封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {manga.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${genreToSlug[genre] || genre}`}
                      className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {genre}
                    </Link>
                  ))}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    manga.status === '连载中'
                      ? 'bg-accent-100 text-accent-800'
                      : 'bg-secondary-200 text-secondary-700'
                  }`}>
                    {manga.status}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line text-foreground-400"></i>
                    </div>
                    <span className="text-foreground-600"><strong>作者：</strong>{manga.author}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="text-foreground-600 font-semibold">{manga.rating}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-foreground-400"></i>
                    </div>
                    <span className="text-foreground-600">{(manga.viewCount / 10000).toFixed(0)}万阅读</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-foreground-400"></i>
                    </div>
                    <span className="text-foreground-600">更新于 {manga.updatedAt}</span>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-8">
                  <h2 className="text-sm font-bold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{manga.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="flex-1 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-circle-line"></i>
                    </div>
                    开始阅读第{manga.chapters[0]?.chapterNumber || 1}话
                  </button>
                  <button
                    type="button"
                    className="px-6 py-3 rounded-full border border-primary-200 text-primary-700 text-sm font-semibold hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bookmark-line"></i>
                    </div>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-14">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-list-check text-primary-500"></i>
                </div>
                章节目录
                <span className="text-sm font-normal text-foreground-500">（共{manga.chapters.length}话）</span>
              </h2>
              <div className="bg-background-100 rounded-2xl overflow-hidden">
                <div className="divide-y divide-background-200">
                  {manga.chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      type="button"
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-background-200/50 transition-colors duration-200 cursor-pointer text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-foreground-400"></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-900 truncate">{chapter.title}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-foreground-500">{chapter.updatedAt}</span>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-foreground-400"></i>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {relatedMangas.length > 0 && (
              <section className="mt-14">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-heart-line text-primary-500"></i>
                  </div>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {relatedMangas.map((rm) => (
                    <Link
                      key={rm.id}
                      to={`/manga/${rm.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-2">
                        <img
                          src={rm.coverImage}
                          alt={rm.title}
                          title={rm.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-sm font-bold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                        {rm.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{rm.author}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
